import { Search, Bell, Settings, Plus, X } from "lucide-react";
import { FiChevronUp, FiChevronDown, FiGrid } from "react-icons/fi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "src/context";
import { useFilter } from "src/context/FilterContext";
import { useActiveFilters } from "src/context/ActiveFiltersContext";
import { getCookie, languageStorage, setLocalStorage } from "src/helpers/localStorage";
import { getUrlOpposite, getUrlOppositeActivityDetails } from "src/helpers/utils";
import { cn } from "src/lib/utils";
import { gql } from "@apollo/client";
import getLoginId from "@zeliot/common/utils/getLoginId";
import client from "src/api/apollo/apolloClient";
import { Menu } from "@headlessui/react"; // Pour le menu deroulant
import SpecialFiltersModal from "./SpecialFiltersModal";
import { TbFilterCog } from "react-icons/tb";
import AoiTree from "./AoiTree";
import { flattenTree } from "./aoiTreeUtils";
import englishFlag from "src/assets/images/en.png";
import frenchFlag from "src/assets/images/fr.png";
import {
  FaGraduationCap,
  FaHome,
  FaUser,
  FaCoffee,
  FaShoppingBag,
  FaShieldAlt,
  FaMapPin,
  FaHeart,
  FaBus,
  FaCarSide,
  FaMotorcycle,
  FaTruck,
  FaShuttleVan,
  FaBoxOpen,
  FaBuilding
} from 'react-icons/fa';
import { useTranslation } from "src/pages";


const MultiSelectField = ({ label, values, setValues, options, icon: Icon, iconClassName = "text-[#2563eb]", toggleArraySelection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");

  const filteredOptions = localSearch.trim()
    ? options.filter((item) => String(item?.name || "").toLowerCase().includes(localSearch.trim().toLowerCase()))
    : options;

  const hasSelection = values.length > 0;

  return (
    <div className="rounded-[14px] border border-[#d9deea] bg-white shadow-sm overflow-hidden ">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#f8fafc] transition-all"
      >
        <div className="flex items-center gap-2.5">
          <Icon className={`h-4 w-4 ${hasSelection ? iconClassName : "text-[#94a3b8]"}`} />
          <span className={`text-[0.88rem] font-semibold ${hasSelection ? "text-[#24324d]" : "text-[#64748b]"}`}>
            {label}
          </span>
          {hasSelection && (
            <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#eef4ff] text-[0.72rem] font-bold text-[#1f5ed6]">
              {values.length}
            </span>
          )}
        </div>
        <svg
          className={`h-4 w-4 text-[#94a3b8] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {hasSelection && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5 border-t border-[#f1f5f9] pt-2">
          {values.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => toggleArraySelection(value, setValues)}
              className="inline-flex items-center gap-1 rounded-lg bg-[#eef4ff] border border-[#c7d9ff] px-2 py-0.5 text-[0.78rem] font-medium text-[#1f5ed6] hover:bg-[#dbeafe] transition-all"
            >
              <span className="h-3 w-3 rounded-sm bg-[#1f5ed6] flex items-center justify-center flex-shrink-0">
                <svg className="h-2 w-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              {value}
              <span className="text-[#7c8ba3] ml-0.5 hover:text-red-400">×</span>
            </button>
          ))}
        </div>
      )}

      {isOpen && (
        <div className="border-t border-[#f1f5f9]">
          <div className="px-3 py-2 bg-[#f8fafc]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94a3b8]" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={localSearch}
                className="w-full pl-8 pr-2 py-1.5 text-[0.8rem] border border-[#e3e8f1] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1f5ed6]/20 focus:border-[#1f5ed6]"
                onChange={(e) => setLocalSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="max-h-[140px] overflow-y-auto bg-white">
            {filteredOptions.map((option) => {
              const isSelected = values.includes(option.name);
              return (
                <button
                  key={option.id || option.name}
                  type="button"
                  onClick={() => toggleArraySelection(option.name, setValues)}
                  className={`flex w-full items-center justify-between px-4 py-2 text-left text-[0.82rem] transition-all hover:bg-[#f8fafc] ${
                    isSelected ? "bg-[#f0f5ff] text-[#1f5ed6] font-medium" : "text-[#475569]"
                  }`}
                >
                  <span>{option.name}</span>
                  {isSelected && (
                    <svg className="h-3.5 w-3.5 text-[#1f5ed6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="px-4 py-3 text-[0.82rem] text-[#94a3b8] text-center">Aucun résultat</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = ({ allAreas, onAddFilter, setDrawAllAois ,onAddFilterzone}) => {
  const { isPanelOpen, togglePanel } = useFilter();
  const { activeFilters, addFilter, removeFilter, clearFilters, currentSelection } = useActiveFilters();
  const [isSpecialFiltersModalOpen, setIsSpecialFiltersModalOpen] = useState(false);
  const [isZoneFilterModalOpen, setIsZoneFilterModalOpen] = useState(false);
  const [shouldRestorePanelState, setShouldRestorePanelState] = useState(false);
  const [selectedSiteForDisplay, setSelectedSiteForDisplay] = useState(null); // Track selected site to display
  const [previousSites, setPreviousSites] = useState([]); // Track previous sites to show one of them
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const [aoiSearchTerm, setAoiSearchTerm] = useState("");
  const [modalFilterSearch, setModalFilterSearch] = useState("");
  const [modalRegion, setModalRegion] = useState("");
  const [modalDepartements, setModalDepartements] = useState([]);
  const [modalArrondissements, setModalArrondissements] = useState([]);
  const [modalQuartiers, setModalQuartiers] = useState([]);
  const [modalZoneInterets, setModalZoneInterets] = useState([]);
  const [modalVehicleType, setModalVehicleType] = useState("Tous");
  const [selectedSpecialFilter, setSelectedSpecialFilter] = useState({                                                  
    'id': 'administrative-zones',                                                                                         
    'name': 'Zone Administrative',                                                                                        
    'icon': 'FaMapPin',                                                                                                   
    'color': 'text-blue-500',                                                                                             
    'bgColor': 'bg-blue-50',                                                                                              
    'borderColor': 'border-blue-200'                                                                                      
  });  
  const getInitials = (name) => {
    if (!name) return "";
    const words = name.split(" ");
    const initials = words.map(word => word.charAt(0).toUpperCase()).join("");
    return initials.substring(0, 2);
  };


  const { language, updateLanguage } = useLanguage();
  const intl = useIntl();
  const { pathname, state } = useLocation();
  const token = getCookie("token");
  let history = useNavigate();

  const english = intl.formatMessage({ id: "english" });
  const french = intl.formatMessage({ id: "french" });
  let urlOpposite = null;

  const changeLanguage = (value) => {
    setLocalStorage(languageStorage, value);
    updateLanguage();
    if (`${pathname}`.includes("activity-details") || `${pathname}`.includes("details-activite")) {
      urlOpposite = getUrlOppositeActivityDetails(pathname);
    } else {
      urlOpposite = getUrlOpposite(pathname);
    }
    history(urlOpposite, { replace: true, state: state });
  };
  console.log(allAreas)
  console.log(allAreas.length)

  // Separer Overview + sites visibles + sites du menu
  const sites = [{ name: "Overview", parentId: null }, ...allAreas.filter(a => a.parentId === null)];
  const aoiTreeRoots = useMemo(
    () => (Array.isArray(allAreas) ? allAreas.filter((a) => a?.parentId === null || a?.parentId === undefined) : []),
    [allAreas],
  );
  const filteredAoiRoots = useMemo(() => {
    const query = aoiSearchTerm.trim().toLowerCase();
    if (!query) return aoiTreeRoots;

    const filterTree = (nodes) =>
      (Array.isArray(nodes) ? nodes : [])
        .map((node) => {
          const children = filterTree(node.children || []);
          const selfMatch = String(node?.name || "").toLowerCase().includes(query);
          if (selfMatch || children.length > 0) {
            return { ...node, children };
          }
          return null;
        })
        .filter(Boolean);

    return filterTree(aoiTreeRoots);
  }, [aoiSearchTerm, aoiTreeRoots]);
  const totalAoiNodes = useMemo(() => flattenTree(aoiTreeRoots).length, [aoiTreeRoots]);
  const normalizedAreas = useMemo(() => (Array.isArray(allAreas) ? allAreas : []), [allAreas]);
  const getNodeChildren = useCallback((node) => {
    if (!node) return [];
    if (Array.isArray(node.children) && node.children.length > 0) {
      return node.children;
    }
    return normalizedAreas.filter((area) => String(area?.parentId) === String(node?.id));
  }, [normalizedAreas]);
  const uniqueAreasById = useCallback((areas) => {
    const seen = new Set();
    return (Array.isArray(areas) ? areas : []).filter((area) => {
      const key = String(area?.id ?? area?.name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);
  const collectAreasAtDepth = useCallback((nodes, targetDepth) => {
    const walk = (currentNodes, currentDepth) => {
      if (!Array.isArray(currentNodes) || currentNodes.length === 0) return [];
      if (currentDepth === targetDepth) return currentNodes;
      return currentNodes.flatMap((node) => walk(getNodeChildren(node), currentDepth + 1));
    };

    return uniqueAreasById(walk(nodes, 0));
  }, [getNodeChildren, uniqueAreasById]);
  const findAreaByName = useCallback((name, sourceList) => {
    if (!name) return null;
    const normalizedName = String(name).trim().toLowerCase();
    return (sourceList || normalizedAreas).find(
      (area) => String(area?.name || "").trim().toLowerCase() === normalizedName
    ) || null;
  }, [normalizedAreas]);
  const locationRegions = useMemo(
    () => normalizedAreas.filter((a) => a?.parentId === null || a?.parentId === undefined),
    [normalizedAreas]
  );
  const selectedModalRegionNode = useMemo(
    () => findAreaByName(modalRegion, locationRegions),
    [findAreaByName, locationRegions, modalRegion]
  );
  const selectedModalDepartmentNodes = useMemo(
    () => modalDepartements.map((name) => findAreaByName(name)).filter(Boolean),
    [findAreaByName, modalDepartements]
  );
  const selectedModalArrondissementNodes = useMemo(
    () => modalArrondissements.map((name) => findAreaByName(name)).filter(Boolean),
    [findAreaByName, modalArrondissements]
  );
  const regionScopedRoots = useMemo(() => {
    return selectedModalRegionNode ? [selectedModalRegionNode] : locationRegions;
  }, [locationRegions, selectedModalRegionNode]);
  const locationDepartements = useMemo(() => {
    return collectAreasAtDepth(regionScopedRoots, 1);
  }, [collectAreasAtDepth, regionScopedRoots]);
  const locationArrondissements = useMemo(() => {
    if (selectedModalDepartmentNodes.length > 0) {
      return collectAreasAtDepth(selectedModalDepartmentNodes, 1);
    }
    return collectAreasAtDepth(regionScopedRoots, 2);
  }, [collectAreasAtDepth, regionScopedRoots, selectedModalDepartmentNodes]);
  const locationQuartiers = useMemo(() => {
    if (selectedModalArrondissementNodes.length > 0) {
      return collectAreasAtDepth(selectedModalArrondissementNodes, 2);
    }
    if (selectedModalDepartmentNodes.length > 0) {
      return collectAreasAtDepth(selectedModalDepartmentNodes, 3);
    }
    return collectAreasAtDepth(regionScopedRoots, 4);
  }, [collectAreasAtDepth, regionScopedRoots, selectedModalArrondissementNodes, selectedModalDepartmentNodes]);
  const locationZoneInterets = useMemo(() => {
    if (selectedModalArrondissementNodes.length > 0) {
      return collectAreasAtDepth(selectedModalArrondissementNodes, 3);
    }
    if (selectedModalDepartmentNodes.length > 0) {
      return collectAreasAtDepth(selectedModalDepartmentNodes, 4);
    }
    return collectAreasAtDepth(regionScopedRoots, 5);
  }, [collectAreasAtDepth, regionScopedRoots, selectedModalArrondissementNodes, selectedModalDepartmentNodes]);
  const locationStats = useMemo(() => ({
    departements: locationDepartements.length,
    arrondissements: locationArrondissements.length,
    quartiers: locationQuartiers.length,
    zonesInteret: locationZoneInterets.length,
  }), [locationArrondissements.length, locationDepartements.length, locationQuartiers.length, locationZoneInterets.length]);
  const vehicleTypeOptions = useMemo(() => ([
    { label: "Tous", value: "Tous", icon: FiGrid, iconClassName: "text-white", activeClassName: "bg-[#1565f7] text-white border-[#1565f7] shadow-[0_16px_30px_rgba(21,101,247,0.28)]" },
    { label: "Bus scolaire", value: "Bus scolaire", icon: FaBus, iconClassName: "text-[#18a566]" },
    { label: "Taxi", value: "Taxi", icon: FaCarSide, iconClassName: "text-[#f59e0b]" },
    { label: "Moto", value: "Moto", icon: FaMotorcycle, iconClassName: "text-[#0f766e]" },
    { label: "Mini Bus", value: "Mini Bus", icon: FaShuttleVan, iconClassName: "text-[#7c3aed]" },
    { label: "Camion", value: "Camion", icon: FaTruck, iconClassName: "text-[#ef4444]" },
    { label: "Cargo", value: "Cargo", icon: FaBoxOpen, iconClassName: "text-[#2563eb]" },
  ]), []);
  const selectedAdministrativeFilters = useMemo(
    () =>
      activeFilters.filter((filter) => {
        const type = String(filter?.type || "");
        return type === "special-item" || type.startsWith("special-item-");
      }),
    [activeFilters]
  );

  // Initialize previous sites when sites are loaded and no selection has been made yet
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteForDisplay && previousSites.length === 0) {
      // Set the first 2 sites after Overview as previous sites (to have something to show later)
      const initialPreviousSites = sites.slice(1, 3); // Skip Overview, take next 2
      setPreviousSites(initialPreviousSites);
    }
  }, [sites, selectedSiteForDisplay, previousSites.length]);

  // Determine visible sites: show Overview, selected site, and one previous site
  let visibleSites;
  if (selectedSiteForDisplay) {
    // Find the selected site in the sites array
    const selectedSite = sites.find(site => site.name === selectedSiteForDisplay);
    if (selectedSite) {
      // Start with Overview and the selected site
      visibleSites = [sites[0], selectedSite]; // Overview + selected site

      // Add one site from previous sites if available and not already included
      const prevSite = previousSites.find(prev =>
        prev.name !== sites[0].name && prev.name !== selectedSite.name &&
        !visibleSites.some(vis => vis.name === prev.name)
      );

      if (prevSite && visibleSites.length < 3) {
        visibleSites.push(prevSite);
      } else if (visibleSites.length < 3) {
        // If no previous site available or doesn't meet criteria, add first available site
        const additionalSite = sites.find(site =>
          site.name !== sites[0].name && site.name !== selectedSite.name &&
          !visibleSites.some(vis => vis.name === site.name)
        );
        if (additionalSite) {
          visibleSites.push(additionalSite);
        }
      }
    } else {
      // If selected site not found, fallback to default behavior
      visibleSites = sites.slice(0, Math.min(3, sites.length));
    }
  } else {
    // Default: show first 3 sites (Overview + 2 others) when no site is selected
    visibleSites = sites.slice(0, Math.min(3, sites.length));
  }

  // Hidden sites are all sites not in visibleSites
  const hiddenSites = sites.filter(site =>
    !visibleSites?.some(vis => vis.name === site.name)
  );
  
  const handleAreaSelection = useCallback((area) => {
    const currentVisibleWithoutOverview = visibleSites?.filter(site => site.name !== "Overview") || [];
    setPreviousSites(currentVisibleWithoutOverview);
    setSelectedSiteForDisplay(area.name === "Overview" ? null : area.name);

    if (setDrawAllAois && typeof setDrawAllAois === 'function') {
      setDrawAllAois(area);
    }

    const clearAdministrativeFilters = () => {
      const administrativeFilters = activeFilters.filter((f) => {
        const type = String(f?.type || "");
        const isSpecial = type === "special-item" || type.startsWith("special-item-");
        const isZone = type === "zone";
        return isSpecial || isZone;
      });
      administrativeFilters.forEach((f) => removeFilter(f));
    };

    if (area.name === "Overview") {
      clearAdministrativeFilters();
    }

    let filterType = "site";
    let filterCategory = null;
    let filterValue = area.name === "Overview" ? "all" : area.name;
    let filterLabel = area.name === "Overview" ? "Tous les sites" : area.name;

    const normalizedName = String(area?.name || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    const isRootAoiRegion =
      area.name !== "Overview" &&
      (area?.parentId === null || area?.parentId === undefined) &&
      aoiTreeRoots.some((root) => String(root?.id) === String(area?.id));

    if (isRootAoiRegion) {
      filterType = "special-item";
      filterCategory = "administrative-zones";
    } else if (normalizedName.includes("arrondissement")) {
      filterType = "special-item";
      filterCategory = "arrondissement";
    } else if (normalizedName.includes("district")) {
      filterType = "special-item";
      filterCategory = "district";
    } else if (normalizedName.includes("quartier")) {
      filterType = "special-item";
      filterCategory = "quartier";
    } else if (
      normalizedName.includes("lieu") ||
      normalizedName.includes("place") ||
      normalizedName.includes("hopital") ||
      normalizedName.includes("ecole") ||
      normalizedName.includes("marche") ||
      normalizedName.includes("banque") ||
      normalizedName.includes("pharmacie") ||
      normalizedName.includes("stade") ||
      normalizedName.includes("universite") ||
      normalizedName.includes("gare") ||
      normalizedName.includes("mairie") ||
      normalizedName.includes("mosquee") ||
      normalizedName.includes("plage") ||
      normalizedName.includes("hotel") ||
      normalizedName.includes("restaurant")
    ) {
      filterType = "special-item";
      filterCategory = "lieu";
    }

    if (filterType === "special-item" && filterCategory) {
      clearAdministrativeFilters();

      const payload = {
        type: filterType,
        value: filterValue,
        label: filterLabel,
        category: filterCategory,
        itemId: area.id || area.name.toLowerCase().replace(/\s+/g, '-'),
      };
      addFilter(payload);

      if (filterCategory === "administrative-zones") {
        const sitePayload = {
          type: "site",
          value: area.name,
          label: area.name,
          area: area,
        };
        onAddFilter(sitePayload);
        addFilter(sitePayload);
      } else {
        onAddFilter(payload);
      }

      return;
    }

    const payload = {
      type: "site",
      value: area.name === "Overview" ? "all" : area.name,
      label: area.name === "Overview" ? "Tous les sites" : area.name,
      area: area,
    };
    onAddFilter(payload);
    addFilter(payload);
  }, [activeFilters, addFilter, onAddFilter, removeFilter, setDrawAllAois, visibleSites]);
  

  const handleSelectSpecialFilter = (filter) => {
    setSelectedSpecialFilter(filter);
    addFilter({
      type: 'special',
      value: filter.name,
      label: filter.name,
      category: filter.id
    });

    onAddFilterzone({
      type: 'special',
      value: filter.name,
      label: filter.name,
      category: filter.id
    });

    setIsSpecialFiltersModalOpen(false);
  };

  const handleOpenZoneFilterModal = useCallback(() => {
    if (!isPanelOpen) {
      togglePanel();
      setShouldRestorePanelState(true);
    } else {
      setShouldRestorePanelState(false);
    }
    setIsZoneFilterModalOpen(true);
  }, [isPanelOpen, togglePanel]);

  const handleCloseZoneFilterModal = useCallback(() => {
    setIsZoneFilterModalOpen(false);
    if (shouldRestorePanelState) {
      togglePanel();
      setShouldRestorePanelState(false);
    }
  }, [shouldRestorePanelState, togglePanel]);

  useEffect(() => {
    if (!isZoneFilterModalOpen) return;
    setModalFilterSearch("");
    setModalRegion(
      selectedAdministrativeFilters.find((filter) => filter?.category === "administrative-zones")?.label ||
        currentSelection?.region ||
        ""
    );
    setModalDepartements(
      selectedAdministrativeFilters.filter((filter) => filter?.category === "departement").map((filter) => filter.label)
    );
    setModalArrondissements(
      selectedAdministrativeFilters.filter((filter) => filter?.category === "arrondissement").map((filter) => filter.label)
    );
    setModalQuartiers(
      selectedAdministrativeFilters.filter((filter) => filter?.category === "quartier").map((filter) => filter.label)
    );
    setModalZoneInterets(
      selectedAdministrativeFilters.filter((filter) => filter?.category === "lieu").map((filter) => filter.label)
    );
    setModalVehicleType(currentSelection?.vehicleType || "Tous");
  }, [currentSelection, isZoneFilterModalOpen, selectedAdministrativeFilters]);

  useEffect(() => {
    setModalDepartements((prev) => prev.filter((name) => locationDepartements.some((item) => item?.name === name)));
  }, [locationDepartements]);

  useEffect(() => {
    setModalArrondissements((prev) => prev.filter((name) => locationArrondissements.some((item) => item?.name === name)));
  }, [locationArrondissements]);

  useEffect(() => {
    setModalQuartiers((prev) => prev.filter((name) => locationQuartiers.some((item) => item?.name === name)));
  }, [locationQuartiers]);

  useEffect(() => {
    setModalZoneInterets((prev) => prev.filter((name) => locationZoneInterets.some((item) => item?.name === name)));
  }, [locationZoneInterets]);

  const filteredLocationOptions = useCallback((options) => {
    const query = modalFilterSearch.trim().toLowerCase();
    if (!query) return options;
    return options.filter((item) => String(item?.name || "").toLowerCase().includes(query));
  }, [modalFilterSearch]);

  const toggleArraySelection = useCallback((value, setter) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  }, []);

  const handleApplyDashboardFilters = useCallback(() => {
    const buildSpecialPayload = (value, category, sourceList, allowMany = false) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return [];
      const values = Array.isArray(value) ? value : [value];
      return values.map((entry) => {
        const match = sourceList.find((item) => item?.name === entry);
        const itemId = match?.id || entry;
        return {
          type:
            category === "administrative-zones"
              ? "special-item"
              : allowMany
              ? `special-item-${category}-${String(itemId)}`
              : `special-item-${category}`,
          value: entry,
          label: entry,
          category,
          itemId,
        };
      });
    };

    const selectedRegionMatch = locationRegions.find((item) => item?.name === modalRegion);
    const sitePayload =
      selectedRegionMatch && modalRegion
        ? {
            type: "site",
            value: modalRegion,
            label: modalRegion,
            area: selectedRegionMatch,
          }
        : null;

    const nextFilters = [
      ...buildSpecialPayload(modalRegion, "administrative-zones", locationRegions),
      ...buildSpecialPayload(modalDepartements, "departement", locationDepartements, true),
      ...buildSpecialPayload(modalArrondissements, "arrondissement", locationArrondissements, true),
      ...buildSpecialPayload(modalQuartiers, "quartier", locationQuartiers, true),
      ...buildSpecialPayload(modalZoneInterets, "lieu", locationZoneInterets, true),
      ...(modalVehicleType && modalVehicleType !== "Tous"
        ? [{ type: "vehicle", value: modalVehicleType, label: modalVehicleType }]
        : []),
    ];

    clearFilters();

    if (sitePayload) {
      addFilter(sitePayload);
      onAddFilter?.(sitePayload);
    }

    nextFilters.forEach((filter) => {
      addFilter(filter);
      if (filter.type === "special-item" || String(filter.type).startsWith("special-item-")) {
        onAddFilterzone?.(filter);
      }
    });

    handleCloseZoneFilterModal();
  }, [
    addFilter,
    clearFilters,
    handleCloseZoneFilterModal,
    locationArrondissements,
    locationDepartements,
    locationQuartiers,
    locationRegions,
    locationZoneInterets,
    modalArrondissements,
    modalDepartements,
    modalQuartiers,
    modalRegion,
    modalVehicleType,
    modalZoneInterets,
    onAddFilter,
    onAddFilterzone,
  ]);

  const handleResetDashboardFilters = useCallback(() => {
    clearFilters();
    setModalFilterSearch("");
    setModalRegion("");
    setModalDepartements([]);
    setModalArrondissements([]);
    setModalQuartiers([]);
    setModalZoneInterets([]);
    setModalVehicleType("Tous");
  }, [clearFilters]);

  // Add the default filter on component mount
  useEffect(() => {
    if (selectedSpecialFilter) {
      addFilter({
        type: 'special',
        value: selectedSpecialFilter.name,
        label: selectedSpecialFilter.name,
        category: selectedSpecialFilter.id
      });

      onAddFilterzone({
        type: 'special',
        value: selectedSpecialFilter.name,
        label: selectedSpecialFilter.name,
        category: selectedSpecialFilter.id
      });
    }
  }, []);

  // const renderMultiSelectField = ({ label, values, setValues, options, icon: Icon, iconClassName = "text-[#2563eb]" }) => {
  //   const filteredOptions = filteredLocationOptions(options);
  //   const hasSelection = values.length > 0;
  //   const [isOpen, setIsOpen] = useState(false);

  //   return (
  //     <div className="rounded-[14px] border border-[#d9deea] bg-white shadow-sm overflow-hidden">
  //       {/* Header - toujours visible */}
  //       <button
  //         type="button"
  //         onClick={() => setIsOpen(!isOpen)}
  //         className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#f8fafc] transition-all"
  //       >
  //         <div className="flex items-center gap-2.5">
  //           <Icon className={`h-4 w-4 ${hasSelection ? iconClassName : "text-[#94a3b8]"}`} />
  //           <span className={`text-[0.88rem] font-semibold ${hasSelection ? "text-[#24324d]" : "text-[#64748b]"}`}>
  //             {label}
  //           </span>
  //           {hasSelection && (
  //             <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#eef4ff] text-[0.72rem] font-bold text-[#1f5ed6]">
  //               {values.length}
  //             </span>
  //           )}
  //         </div>
  //         <svg
  //           className={`h-4 w-4 text-[#94a3b8] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
  //           fill="none" viewBox="0 0 24 24" stroke="currentColor"
  //         >
  //           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  //         </svg>
  //       </button>

  //       {/* Tags des sélections — visibles si sélection ET ouvert OU si fermé avec sélection */}
  //       {hasSelection && (
  //         <div className="px-3 pb-2 flex flex-wrap gap-1.5 border-t border-[#f1f5f9] pt-2">
  //           {values.map((value) => (
  //             <button
  //               key={value}
  //               type="button"
  //               onClick={() => toggleArraySelection(value, setValues)}
  //               className="inline-flex items-center gap-1 rounded-lg bg-[#eef4ff] border border-[#c7d9ff] px-2 py-0.5 text-[0.78rem] font-medium text-[#1f5ed6] hover:bg-[#dbeafe] transition-all"
  //             >
  //               <span className="h-3 w-3 rounded-sm bg-[#1f5ed6] flex items-center justify-center flex-shrink-0">
  //                 <svg className="h-2 w-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
  //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  //                 </svg>
  //               </span>
  //               {value}
  //               <span className="text-[#7c8ba3] ml-0.5 hover:text-red-400">×</span>
  //             </button>
  //           ))}
  //         </div>
  //       )}

  //       {/* Dropdown ouvert */}
  //       {isOpen && (
  //         <div className="border-t border-[#f1f5f9]">
  //           {/* Search inside dropdown */}
  //           <div className="px-3 py-2 bg-[#f8fafc]">
  //             <div className="relative">
  //               <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94a3b8]" />
  //               <input
  //                 type="text"
  //                 placeholder="Rechercher..."
  //                 className="w-full pl-8 pr-2 py-1.5 text-[0.8rem] border border-[#e3e8f1] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1f5ed6]/20 focus:border-[#1f5ed6]"
  //                 onChange={(e) => setModalFilterSearch(e.target.value)}
  //                 onClick={(e) => e.stopPropagation()}
  //               />
  //             </div>
  //           </div>

  //           {/* Options list */}
  //           <div className="max-h-[140px] overflow-y-auto bg-white">
  //             {filteredOptions.map((option) => {
  //               const isSelected = values.includes(option.name);
  //               return (
  //                 <button
  //                   key={option.id || option.name}
  //                   type="button"
  //                   onClick={() => toggleArraySelection(option.name, setValues)}
  //                   className={`flex w-full items-center justify-between px-4 py-2 text-left text-[0.82rem] transition-all hover:bg-[#f8fafc] ${
  //                     isSelected ? "bg-[#f0f5ff] text-[#1f5ed6] font-medium" : "text-[#475569]"
  //                   }`}
  //                 >
  //                   <span>{option.name}</span>
  //                   {isSelected && (
  //                     <svg className="h-3.5 w-3.5 text-[#1f5ed6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
  //                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  //                     </svg>
  //                   )}
  //                 </button>
  //               );
  //             })}
  //             {filteredOptions.length === 0 && (
  //               <div className="px-4 py-3 text-[0.82rem] text-[#94a3b8] text-center">Aucun résultat</div>
  //             )}
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

const {t} = useTranslation();
  return (
    <>
    <header className="relative z-30 h-[50px] border-b border-[#10b981] bg-[#10b981]">
      <div className="flex h-full items-center justify-between gap-3 px-3">
        

        <div className="flex flex-1 justify-center">
          <div className="flex h-[28px] w-full max-w-[438px] items-center rounded-[8px] border border-[#b9d1e5] bg-white px-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.75)]">
            <Search className="h-[14px] w-[14px] text-[#b7c0cb]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-full flex-1 bg-transparent px-2 text-[12px] text-[#334155] outline-none border border-red mx-4"
            />
            <div className="flex items-center gap-1">
              <button type="button" className="h-[18px] min-w-[57px] rounded-[3px] border border-[#d8dee8] bg-[#f7f9fc] px-2 text-[11px] text-[#334155]">
                Device
              </button>
              <button type="button" className="h-[18px] min-w-[48px] rounded-[3px] border border-[#d8dee8] bg-[#f7f9fc] px-2 text-[11px] text-[#334155]">
                Users
              </button>
            </div>
          </div>
        </div>

        <div className="flex min-w-[325px] items-center justify-end gap-3 text-white">
          <button type="button" className="flex h-[24px] w-[24px] items-center justify-center text-[16px]">
            ✉
          </button>
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-1 text-white">
              <img
                src={language === "fr" ? frenchFlag : englishFlag}
                alt={language === "fr" ? "French" : "English"}
                className="h-[14px] w-[22px] rounded-[1px] border border-white/25 object-cover"
              />
              <span className="text-[11px]">▼</span>
            </Menu.Button>
            <Menu.Items className="absolute right-0 top-full z-[999999] mt-2 min-w-[118px] overflow-hidden rounded-[6px] border border-[#d6dee8] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.16)]">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => changeLanguage("en")}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] ${
                      active ? "bg-[#f4f8fc]" : "bg-white"
                    } ${language === "en" ? "text-[#198cff]" : "text-[#334155]"}`}
                  >
                    <img src={englishFlag} alt="English" className="h-[12px] w-[18px] rounded-[1px] border border-[#d9e2ec] object-cover" />
                    <span>{english}</span>
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => changeLanguage("fr")}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] ${
                      active ? "bg-[#f4f8fc]" : "bg-white"
                    } ${language === "fr" ? "text-[#198cff]" : "text-[#334155]"}`}
                  >
                    <img src={frenchFlag} alt="French" className="h-[12px] w-[18px] rounded-[1px] border border-[#d9e2ec] object-cover" />
                    <span>{french}</span>
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Menu>
          <button type="button" className="flex h-[24px] w-[24px] items-center justify-center">
            <Settings className="h-[15px] w-[15px] text-[#ffff]"  />
          </button>
          <div className="flex items-center gap-2">
            <div className="flex h-[28px] w-[28px] items-center justify-center rounded-full border border-white text-[13px]">
            <FaUser className="h-[15px] w-[15px] text-[#ffff]" />
            </div>
            <span className="max-w-[120px] truncate text-[13px] font-medium">
              {getCookie("email") || "africsys@afri..."}
            </span>
            <span className="text-[11px]">▼</span>
          </div>
        </div>
      </div>
    </header>

     {/* Modal des filtres speciaux */}
     <div style={{ zIndex: 99999999 }}>
       <SpecialFiltersModal
       isOpen={isSpecialFiltersModalOpen}
       onClose={() => setIsSpecialFiltersModalOpen(false)}
       onSelectFilter={handleSelectSpecialFilter}
       selectedFilter={selectedSpecialFilter}
     />
     </div>

     {isZoneFilterModalOpen ? (
       <div className="fixed inset-0 z-[99999998] flex items-center justify-center bg-black/45 p-4">
         <div className="w-full max-w-5xl overflow-hidden rounded-[24px] border border-[#d8deea] bg-[#f6f7fb] shadow-[0_24px_64px_rgba(15,23,42,0.2)]">
           <div className="border-b border-[#e5eaf2] px-5 py-4">
             <div className="flex items-center justify-between gap-4">
               <div className="flex items-center gap-3 text-[#23314d]">
                 <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white shadow-sm">
                   <TbFilterCog className="h-4.5 w-4.5" />
                 </div>
                 <div>
                   <h3 className="text-[1rem] font-semibold tracking-[0.04em] uppercase">
                     {modalRegion ? `Filtres Du Dashboard - ${modalRegion}` : "Filtres Du Dashboard"}
                   </h3>
                 </div>
               </div>
               <button
                 type="button"
                 onClick={handleCloseZoneFilterModal}
                 className="inline-flex items-center gap-2 rounded-xl border border-[#d4d9e4] bg-white px-3.5 py-2 text-[0.88rem] font-medium text-[#23314d] shadow-sm transition-all hover:bg-[#f8fafc]"
               >
                 <X className="h-4.5 w-4.5" />
                 Fermer
               </button>
             </div>

             <div className="mt-4 flex h-[48px] items-center rounded-xl border border-[#d8deea] bg-white px-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
               <Search className="h-4.5 w-4.5 text-[#5f6f8b]" />
               <input
                 type="text"
                 value={modalFilterSearch}
                 onChange={(e) => setModalFilterSearch(e.target.value)}
                 placeholder={t("LEWOOMINING.search_zone_or_vehicle")}
                 className="ml-3 h-full w-full bg-transparent text-[0.95rem] text-[#2f3d57] placeholder:text-[#7f8ba4] outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
               />
             </div>
           </div>

           <div className="p-4">
             <div className="rounded-[22px] border border-[#e2e7f0] bg-[#f7f8fb] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
               <div className="grid gap-4 lg:grid-cols-[1.08fr_0.92fr]">
                 <div className="pr-0 lg:border-r lg:border-[#e1e6ee] lg:pr-4">
                   <div className="mb-4 flex items-center gap-3 text-[#1f3f8f]">
                     <FaMapPin className="h-4.5 w-4.5" />
                     <h4 className="text-[0.95rem] font-semibold text-[#23314d]">Localisation</h4>
                   </div>

<div className="grid gap-3 sm:grid-cols-2">
  <div className="sm:col-span-2">
    <div className="rounded-[14px] border border-[#d9deea] bg-white p-3.5 shadow-sm">
      <div className="mb-2 flex items-center gap-2.5">
        <FaMapPin className="h-4 w-4 text-[#2563eb]" />
        <span className="text-[0.88rem] font-semibold text-[#24324d]">
          {`Région (${locationRegions.length})`}
        </span>
      </div>
      <select
        value={modalRegion}
        onChange={(e) => {
          setModalRegion(e.target.value);
          setModalDepartements([]);
          setModalArrondissements([]);
          setModalQuartiers([]);
          setModalZoneInterets([]);
        }}
        className="w-full rounded-xl border border-[#d9deea] bg-[#f8fafc] px-3 py-2.5 text-[0.88rem] text-[#24324d] outline-none focus:border-[#1565f7] focus:ring-2 focus:ring-[#1565f7]/15"
      >
        <option value="">Toutes les régions</option>
        {filteredLocationOptions(locationRegions).map((region) => (
          <option key={region.id || region.name} value={region.name}>
            {region.name}
          </option>
        ))}
      </select>
    </div>
  </div>
  <MultiSelectField
    label={`Département (${locationStats.departements})`}
    values={modalDepartements}
    setValues={setModalDepartements}
    options={filteredLocationOptions(locationDepartements)}
    icon={FaBuilding}
    iconClassName="text-[#2563eb]"
    toggleArraySelection={toggleArraySelection}
  />
  <MultiSelectField
    label={`Arrondissement (${locationStats.arrondissements})`}
    values={modalArrondissements}
    setValues={setModalArrondissements}
    options={filteredLocationOptions(locationArrondissements)}
    icon={FaMapPin}
    iconClassName="text-[#10b981]"
    toggleArraySelection={toggleArraySelection}
  />
  <MultiSelectField
    label={`Quartier (${locationStats.quartiers})`}
    values={modalQuartiers}
    setValues={setModalQuartiers}
    options={filteredLocationOptions(locationQuartiers)}
    icon={FaBuilding}
    iconClassName="text-[#7c3aed]"
    toggleArraySelection={toggleArraySelection}
  />
  <MultiSelectField
    label={`Zone d'intérêt (${locationStats.zonesInteret})`}
    values={modalZoneInterets}
    setValues={setModalZoneInterets}
    options={filteredLocationOptions(locationZoneInterets)}
    icon={FaHeart}
    iconClassName="text-[#f59e0b]"
    toggleArraySelection={toggleArraySelection}
  />
</div>
                 </div>

                 <div>
                   <div className="mb-4 flex items-center gap-3 text-[#1f2937]">
                     <FaBus className="h-4.5 w-4.5" />
                   <h4 className="text-[0.95rem] font-semibold text-[#23314d]">
                   {t("LEWOOMINING.vehicle_type")}
                  </h4>
                  </div>

                  <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-2">
                    {vehicleTypeOptions.map((vehicle) => {
                      const Icon = vehicle.icon;
                      const isActive = modalVehicleType === vehicle.value;
                      // Simuler un compteur (à remplacer par vos vraies données)
                      const count = null; // Remplacer par votre logique de comptage réelle

                      return (
                        <button
                          key={vehicle.value}
                          type="button"
                          onClick={() => setModalVehicleType(vehicle.value)}
                          className={`rounded-[18px] border px-3.5 py-3 text-left transition-all flex items-center justify-between ${
                            isActive
                              ? vehicle.activeClassName || "border-[#1565f7] bg-[#1565f7] text-white shadow-[0_16px_30px_rgba(21,101,247,0.28)]"
                              : "border-[#d9deea] bg-white text-[#24324d] shadow-sm hover:border-[#c6d1e3]"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon className={`h-5 w-5 ${isActive ? "text-white" : vehicle.iconClassName}`} />
                            <span className="text-[0.88rem] font-semibold">{vehicle.label}</span>
                          </div>
                          {/* Badge compteur — affiché seulement si des filtres de zone sont actifs */}
                          {selectedAdministrativeFilters.length > 0 && count !== null && (
                            <span className={`text-[0.78rem] font-bold px-1.5 py-0.5 rounded-full ${
                              isActive ? "bg-white/20 text-white" : "bg-[#f1f5f9] text-[#64748b]"
                            }`}>
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                 </div>
               </div>

               <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#e5eaf2] pt-4">
                 <div className="text-[0.84rem] text-[#7c8ba3]">
                   {modalRegion ? `Filtres appliqués sur ${modalRegion}` : "Sélectionnez les filtres et cliquez sur Appliquer"}
                 </div>
 
                 <div className="flex items-center gap-4">
                 <button
                   type="button"
                   onClick={handleResetDashboardFilters}
                   className="rounded-xl border border-[#d5dbe7] bg-white px-5 py-2.5 text-[0.88rem] font-semibold text-[#334155] shadow-sm transition-all hover:bg-[#f8fafc]"
                 >
                   Réinitialiser
                 </button>
                 <button
                   type="button"
                   onClick={handleApplyDashboardFilters}
                   className="rounded-xl bg-[#1565f7] px-6 py-2.5 text-[0.88rem] font-semibold text-white shadow-[0_16px_24px_rgba(21,101,247,0.24)] transition-all hover:bg-[#0f57dc]"
                 >
                   Appliquer
                 </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       </div>
     ) : null}
   </>
  );
};
